import {
    ITtsSynthesizerContext,
    ITtsUtteranceData
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import {ElevenLabsApiClient} from "./ElevenLabsApiClient";
import {ElevenLabsUtterance} from "./ElevenLabsUtterance";
import {ElevenLabsUtteranceAudioElementRenderer} from "./ElevenLabsUtteranceAudioElementRenderer";
import {ISpeechSynthesisVoiceCollection, WebSpeechUtils} from "../../WebSpeechUtils";
import {IVanillaTtsServiceProvider} from "../../../VanillaReader/VanillaReaderModel";
import {IElevenLabsVoiceData} from "./types/IElevenLabsVoiceData";
import {IElevenLabsUserInfoData} from "./types/IElevenLabsUserInfoData";
import {ElevenLabsOutputFormats} from "./types/ElevenLabsOutputFormats";


/**
 *
 * ElevenLabsTtsServiceProvider
 *
 * ElevenLabsTtsServiceProvider is an implementation of the `IVanillaTtsServiceProvider` that integrates with the
 * ElevenLabs (elevenlabs.io) Text To Speech cloud service.
 *
 * By implementing the `IVanillaTtsServiceProvider` interface, this class can be "plugged into" the Vanilla Reader.
 *
 * This class does not rely on any VanillaReader specifics however, and you can use the `ITtsServiceProvider` interface
 * provided in the `types/` directory. Or just remove the interface all together if you don't need it.
 *
 * */

export class ElevenLabsTtsServiceProvider implements IVanillaTtsServiceProvider {
    private _utteranceRenderer: ElevenLabsUtteranceAudioElementRenderer;
    private _utteranceDataQueue: ElevenLabsUtterance[] = [];
    private _utteranceIdToUtteranceInstanceMap: Map<number, ElevenLabsUtterance> = new Map();
    private _activeUtterance: ElevenLabsUtterance | undefined;
    private _activeVoice: SpeechSynthesisVoice | undefined;
    private _colibrioContext: ITtsSynthesizerContext | undefined;
    private _playbackIsPaused: boolean = true;
    private _voiceCollection: ISpeechSynthesisVoiceCollection | undefined;

    constructor(private _apiClient: ElevenLabsApiClient) {

        this._utteranceRenderer = new ElevenLabsUtteranceAudioElementRenderer();

        this._utteranceRenderer.onPlaybackStarted((_ev, _utterance) => {
        });

        this._utteranceRenderer.onPlaybackPaused((_ev, _utterance) => {
        });

        this._utteranceRenderer.onPlaybackEndReached(async (_ev, utterance) => {
            this._colibrioContext?.onUtteranceEnd(utterance.id);
            this._activeUtterance = undefined;
            await this.play();
            utterance.revokeBlobUrl();
        });

        this._utteranceRenderer.onUtteranceBoundary((_ev, utterance, startOffset, length) => {
            this._colibrioContext?.onUtteranceBoundary(utterance.id, startOffset, length);
        });

        this._utteranceRenderer.onPlaybackError((ev, utterance) => {
            this._colibrioContext?.onUtteranceError(utterance.id, ev);
        });
    }

    public get supportsWordBoundaries(): boolean {
        return false;
    }

    getActiveVoice(): SpeechSynthesisVoice | undefined {
        return this._activeVoice;
    }

    addUtterance(utteranceData: ITtsUtteranceData): void {
        const voiceURI = this._activeVoice?.voiceURI || this._apiClient.defaultVoiceId;
        const newUtterance = new ElevenLabsUtterance(voiceURI,
            utteranceData,
            ElevenLabsOutputFormats.mp3_44100_64,
            {
                stability: 0.5,
                similarityBoost: 0.5
            },
            this._apiClient);
        this._utteranceIdToUtteranceInstanceMap.set(utteranceData.id, newUtterance);
        this._utteranceDataQueue.push(newUtterance);
    }

    clearAndPause(): Promise<void> {
        return this._clearPlaybackState();
    }

    destroy(): void {
        this._clearPlaybackState().catch(console.warn);
    }

    init(context: ITtsSynthesizerContext): void {
        this._colibrioContext = context;
        this._colibrioContext.onTtsSynthesizerWaiting();
        this._validateUserSession().then(async (_userInfo) => {
            this._voiceCollection = await this.fetchVoices();
            this._activeVoice = this._getDefaultVoiceDataForService();
            this._colibrioContext?.onTtsSynthesizerReady();
        }).catch(error => {
            console.error(error);
            console.error('ElevenLabsWebSpeechSynthesizer.init(): Unable to validate ElevenLabs user.');
        });
    }

    pause(): void {
        this._playbackIsPaused = true;
        this._utteranceRenderer.pause();
    }

    async play(): Promise<void> {
        this._playbackIsPaused = false;
        if (this._activeUtterance) {
            this._playUtterance(this._activeUtterance).catch(console.warn);
        } else {
            const nextUtterance = this._utteranceDataQueue.shift();
            if (!nextUtterance) {
                console.warn('ElevenLabsWebSpeechSynthesizer.play(): Unable to get next buffered utterance.');
                return;
            }

            this._prefetchUpcomingUtterance();

            this._playUtterance(nextUtterance).catch(console.warn);
        }
    }

    setMuted(muted: boolean): void {
        this._utteranceRenderer.muted = muted;
    }

    setPlaybackRate(playbackRate: number): void {
        this._utteranceRenderer.playbackRate = playbackRate;
    }

    setVolume(volume: number): void {
        this._utteranceRenderer.volume = volume;
    }

    setActiveVoice(voice: SpeechSynthesisVoice | undefined): void {
        const voiceName = voice?.name;
        const ttsVoice = this._voiceCollection?.allVoices.find((voice) => {
            return voice.name === voiceName;
        });

        if (ttsVoice) {
            this._activeVoice = ttsVoice;
        } else {
            console.warn(`ElevenLabsWebSpeechSynthesizer.setVoice(): Unable to find voice with name "${voiceName}", using default voice.`);
            this._activeVoice = this._getDefaultVoiceDataForService();
        }
    }

    async fetchVoices(): Promise<ISpeechSynthesisVoiceCollection> {
        const voiceDataList = await this._apiClient.getVoices();

        const webSpeechVoiceList = voiceDataList.map<SpeechSynthesisVoice>((voiceData: IElevenLabsVoiceData) => {
            return {
                voiceURI: voiceData.voice_id,
                name: voiceData.name,
                lang: '',
                default: (voiceData.voice_id === this._apiClient.defaultVoiceId),
                localService: false
            }
        });

        return WebSpeechUtils.createCollectionFromVoicesList(webSpeechVoiceList);
    }

    private _getDefaultVoiceDataForService(): SpeechSynthesisVoice | undefined {
        return this._voiceCollection?.allVoices.find((voice) => {
            return voice.voiceURI === this._apiClient.defaultVoiceId;
        });

    }

    private async _validateUserSession(): Promise<IElevenLabsUserInfoData> {
        return this._apiClient.getUserInfo();
    }

    private _prefetchUpcomingUtterance(): void {
        if (this._utteranceDataQueue.length < 1) {
            console.log('ElevenLabsWebSpeechSynthesizer._preFetchUpcomingUtterance(), Buffer is empty, nothing to play.');
            return;
        }

        this._utteranceDataQueue[0].fetchSpeechData().catch(console.warn);

    }

    private async _playUtterance(utterance: ElevenLabsUtterance): Promise<void> {
        this._activeUtterance = utterance;
        await this._utteranceRenderer.setUtterance(utterance);
        if (!this._playbackIsPaused) {
            this._utteranceRenderer.play().catch(console.warn);
        }
    }

    private async _clearPlaybackState(): Promise<void> {
        this._utteranceDataQueue.forEach(utterance => utterance.revokeBlobUrl());
        this._utteranceDataQueue = [];
        await this._utteranceRenderer.setUtterance(undefined);
        this._activeUtterance?.revokeBlobUrl();
        this._activeUtterance = undefined;
        this._playbackIsPaused = true;
    }

    handleEvent(_evt: Event): void {
    }

}
